import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReportMetadata } from '../../../types';
import type { Planungsfrage } from '../../planungsphase/types';
import { heuteLokalIso } from '../../planungsphase/planungLogic';
import {
    baueSteckbrief,
    ladeSteckbriefQuellen,
    lesefehlerText,
    type ProjektSteckbrief,
    type SteckbriefQuellen,
} from '../../../services/projektSteckbrief';

/**
 * Projektsteckbrief fuer die Unterlage fuer spaetere Arbeiten — PLUS die
 * Fragen mit ihren Ids.
 *
 * Warum nicht `useProjektSteckbrief`? Der Steckbrief fuehrt die Fragen ohne
 * `id` (nur Code, Text, Antwort, Stand). Der Stempel „uebernommen"
 * (`markiereUebernommen`) braucht aber `planungsfragen.id`. Statt ein zweites
 * Mal zu laden, liest dieser Hook die Quellen EINMAL (`ladeSteckbriefQuellen`),
 * baut daraus den Steckbrief und behaelt die rohen Fragen daneben.
 *
 * Alle Versprechen des Steckbrief-Hooks gelten auch hier: nur lesen, nichts
 * anlegen, nichts schreiben; `null` vor der Antwort heisst „noch nicht
 * gelesen", nicht „es gibt nichts" — Leisten fragen `bereit` ab; die
 * Projektdaten werden im `useMemo` dazugemischt, nicht im Ladeeffekt (sonst
 * liefe bei jedem Tastendruck in den Stammdaten eine Datenbankrunde los).
 */

interface Gelesen {
    fuer: string;
    quellen: SteckbriefQuellen;
    standTag: string;
}

export interface UfsaPlanungStand {
    /** `null`, solange nicht `bereit`. */
    steckbrief: ProjektSteckbrief | null;
    /** Alle Fragen der Planungsphase mit Id — leer ohne Phase oder vor dem Lesen. */
    fragen: readonly Planungsfrage[];
    /** Erst `true`, wenn die Daten zu GENAU diesem Projekt vorliegen. */
    bereit: boolean;
    /** Lesefehler als Satz; der Steckbrief liegt trotzdem vor (fail-open). */
    fehler: string | null;
    /** Nach „Übernommen" neu lesen — der Stempel steht dann in den Karten. */
    neuLaden: () => void;
}

const KEINE_FRAGEN: readonly Planungsfrage[] = [];

export const useUfsaPlanung = (projectId: string, metadata: Partial<ReportMetadata> | null): UfsaPlanungStand => {
    const [gelesen, setGelesen] = useState<Gelesen | null>(null);
    const [lauf, setLauf] = useState(0);

    const neuLaden = useCallback(() => setLauf(n => n + 1), []);

    useEffect(() => {
        let gilt = true;
        // `ladeSteckbriefQuellen` lehnt nie ab; Lesefehler stehen in den Quellen.
        void ladeSteckbriefQuellen(projectId, null).then(quellen => {
            if (gilt) setGelesen({ fuer: projectId, quellen, standTag: heuteLokalIso() });
        });
        return () => { gilt = false; };
    }, [projectId, lauf]);

    const bereit = gelesen !== null && gelesen.fuer === projectId;

    const steckbrief = useMemo(
        () => (bereit && gelesen ? baueSteckbrief({ ...gelesen.quellen, metadata }, gelesen.standTag) : null),
        [bereit, gelesen, metadata],
    );

    return {
        steckbrief,
        fragen: bereit && gelesen ? gelesen.quellen.fragen : KEINE_FRAGEN,
        bereit,
        fehler: bereit && gelesen ? lesefehlerText(gelesen.quellen.lesefehler) : null,
        neuLaden,
    };
};
